import { api } from './index';

import type { Price, StripeCheckoutSession, User, UserPayment } from '@/types';

export interface StripeCheckoutRequest {
  price: Price;
  user: User;
  successUrl: string;
  cancelUrl: string;
}

export interface ValidateSubscriptionRequest {
  userId: string;
}

export interface CreatePortalLinkRequest {
  userId: string;
}

export interface UpdateCustomerRequest {
  userId: string;
  customerId: string;
}

export const paymentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Create Stripe checkout session through the backend. Do not replace this
    // with a direct Stripe Payment Link from the frontend: the backend must
    // create/link UserPayment.customer_id before subscription webhooks arrive.
    // See docs/payments.md.
    createStripeCheckout: builder.mutation<
      StripeCheckoutSession,
      StripeCheckoutRequest
    >({
      invalidatesTags: ['User'],
      query: ({ price, user, successUrl, cancelUrl }) => {
        const productId =
          import.meta.env.VITE_APP_ENV === 'production'
            ? price.id.production
            : price.id.test;

        return {
          data: {
            cancelUrl,
            productId,
            successUrl,
          },
          method: 'POST',
          url: `/user_payments/${user.id}/stripe_session`,
        };
      },
    }),

    // Validate subscription
    validateSubscription: builder.mutation<
      UserPayment,
      ValidateSubscriptionRequest
    >({
      invalidatesTags: ['User'],
      query: ({ userId }) => ({
        method: 'POST',
        url: `/user_payments/${userId}/validate_subscription`,
      }),
    }),

    // Create customer portal link
    createPortalLink: builder.mutation<
      { url: string },
      CreatePortalLinkRequest
    >({
      query: ({ userId }) => ({
        method: 'POST',
        url: `/user_payments/${userId}/portal_link`,
      }),
    }),

    // Update customer (Admin only)
    updateCustomer: builder.mutation<unknown, UpdateCustomerRequest>({
      invalidatesTags: ['User'],
      query: ({ userId, customerId }) => ({
        data: { customerId },
        method: 'POST',
        url: `/user_payments/${userId}/update_customer`,
      }),
    }),
  }),
});

export const {
  useCreateStripeCheckoutMutation,
  useValidateSubscriptionMutation,
  useCreatePortalLinkMutation,
  useUpdateCustomerMutation,
} = paymentApi;

// Backward compatibility wrappers for existing API pattern
export const useStripeCheckout = () => {
  const [trigger, result] = useCreateStripeCheckoutMutation();
  return {
    mutate: (
      variables: StripeCheckoutRequest,
      options?: {
        onSuccess?: (data: StripeCheckoutSession) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useValidateSubscriptionHook = () => {
  const [trigger, result] = useValidateSubscriptionMutation();
  return {
    mutate: (
      variables: ValidateSubscriptionRequest,
      options?: {
        onSuccess?: (data: UserPayment) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      },
    ) => {
      trigger(variables)
        .unwrap()
        .then((data: UserPayment) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error: unknown) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

// Keep pause payment as a simple utility (not a real API call)
export const pausePayment = async () => Promise.resolve({});
